import React, { useMemo, useState } from 'react'
import { ScrollView, View } from 'react-native'
import { chunk } from 'lodash'
import { useRoute } from '@react-navigation/core'
import { RouteProp } from '@react-navigation/native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@components/base'
import { Product } from '@src/types'
import { SubscriptionItem } from '@src/screens/Marketplace/components/SubscriptionItem'
import { isGroupedProducts } from '@src/screens/Marketplace/types/types'
import { NavigationContainer } from '@src/screens/Common/containers'
import { LoadingContainer } from '@src/screens/Common/containers/LoadingContainer'
import {
  getBasePlan,
  getPriceDescriptorWithPromo,
  getPriceRangeDescriptor,
} from '@src/screens/Marketplace/utils/utils'
import { AppStackParamList, OnboardingStackParamsList } from '@navigation/types'
import { useNavigateToSteloCheckout } from '@screens/Marketplace/utils/hooks.ts'
import { ProgramCard } from '@src/screens/Marketplace/components/ProgramCard'
import { Feature, useFeatureFlag } from '@src/components'
import { useSignUpProducts } from '../hooks'
import { BottomBar } from './SteloSelectSubscription/BottomBar'
import { PromoCard } from './SteloSelectSubscription/PromoCard'

export const SteloSelectSubscription = () => {
  const styles = useStyleSheet(themedStyle)
  const { navigateToSteloCheckout } = useNavigateToSteloCheckout()
  const route = useRoute<
    | RouteProp<AppStackParamList, 'SteloSelectSubscription'>
    | RouteProp<OnboardingStackParamsList, 'SteloSelectSubscription'>
  >()
  const { checkoutKind, subscriptionId } = route.params || {}

  const { groupedProducts } = useSignUpProducts({ reloadProducts: true })

  const insets = useSafeAreaInsets()

  const newProgramDurationDesign = useFeatureFlag(Feature.NewProgramDurationDesign)

  const item = groupedProducts.find((group) => isGroupedProducts(group))

  const preselectedProduct = useMemo(() => item?.products.find((product) => product.preselected), [
    item,
  ])

  const [originalSelectedProduct, setSelectedProduct] = useState<Product | undefined>(
    preselectedProduct,
  )

  let selectedProduct = originalSelectedProduct

  if (!selectedProduct && preselectedProduct) {
    selectedProduct = preselectedProduct
  }

  if (!item) {
    return <LoadingContainer />
  }

  const includedOptions = item.products[0]?.includedOptions || []

  const handleItemPress = (item: Product) => {
    setSelectedProduct(item)
  }

  const baseProduct = getBasePlan(item.products)

  const onNextPress = () => {
    if (!selectedProduct) {
      return
    }

    navigateToSteloCheckout(checkoutKind, subscriptionId, selectedProduct)
  }

  if (!newProgramDurationDesign) {
    return (
      <NavigationContainer style={styles.container} title="Program Duration">
        <ScrollView contentContainerStyle={styles.scrollViewContent} bounces={false}>
          <View style={styles.subscriptions}>
            {chunk(item.products, 2).map((plansPair) => {
              const firstPlan = plansPair[0]
              const secondPlan = plansPair[1] as Product | undefined

              return (
                <View style={styles.plansRow} key={firstPlan.id}>
                  <SubscriptionItem
                    title={firstPlan.title}
                    description={firstPlan.description}
                    item={firstPlan}
                    pill={firstPlan.pill}
                    onItemPress={handleItemPress}
                    selectMode
                    style={styles.subscriptionItem}
                    showPrice={false}
                    selected={firstPlan.id === selectedProduct?.id}
                  />
                  {secondPlan && (
                    <SubscriptionItem
                      title={secondPlan.title}
                      description={secondPlan.description}
                      item={secondPlan}
                      pill={secondPlan.pill}
                      onItemPress={handleItemPress}
                      selectMode
                      showPrice={false}
                      style={styles.subscriptionItem}
                      selected={secondPlan.id === selectedProduct?.id}
                    />
                  )}
                </View>
              )
            })}
          </View>
          {selectedProduct && <PromoCard product={selectedProduct} />}
          {includedOptions.length > 0 && (
            <>
              <Text type="title-3">What you get:</Text>
              <View style={styles.includedOptionsContainer}>
                {includedOptions.map((option) => (
                  <View key={option} style={styles.includedOptionRow}>
                    <Icon name="check" weight="bold" style={styles.icon} />
                    <Text type="regular" style={styles.optionText}>
                      {option}
                    </Text>
                  </View>
                ))}
              </View>
            </>
          )}
        </ScrollView>
        {selectedProduct && (
          <BottomBar
            selectedProduct={selectedProduct}
            baseProduct={baseProduct}
            appliedPromo={selectedProduct.appliedPromo}
            onNextPress={onNextPress}
          />
        )}
      </NavigationContainer>
    )
  }

  return (
    <NavigationContainer style={styles.container} title="Program Duration">
      <ScrollView contentContainerStyle={styles.scrollViewContent}>
        {includedOptions.length > 0 && (
          <>
            <Text type="title-3">What you get:</Text>
            <View style={styles.includedOptionsContainer}>
              {includedOptions.map((option) => (
                <View key={option} style={styles.includedOptionRow}>
                  <Icon name="check" weight="bold" style={styles.icon} />
                  <Text type="regular" style={styles.optionText}>
                    {option}
                  </Text>
                </View>
              ))}
            </View>
          </>
        )}
        <View style={styles.newSubscriptions}>
          {[...item.products].reverse().map((product) => {
            const priceDescriptor = getPriceRangeDescriptor(product)
            const baseProductPriceDescriptor = baseProduct
              ? getPriceRangeDescriptor(baseProduct)
              : undefined
            const promoPriceDescriptor = getPriceDescriptorWithPromo(product, product.appliedPromo)

            const price = product.appliedPromo
              ? promoPriceDescriptor.amount
              : priceDescriptor.amount
            const originalOrBasePrice = product.appliedPromo
              ? promoPriceDescriptor.originalPrice
              : baseProductPriceDescriptor?.amount

            const showCrossedOutPrice =
              !!product.appliedPromo ||
              (baseProductPriceDescriptor && baseProduct && product.price < baseProduct.price)

            return (
              <ProgramCard
                key={product.id}
                product={product}
                onSelect={() => setSelectedProduct(product)}
                selected={selectedProduct?.key === product.key}
                programDetailsTitle="Program details"
                price={
                  <View>
                    <View style={styles.priceContainer}>
                      {showCrossedOutPrice && (
                        <Text type="small" style={styles.secondaryPrice}>
                          {originalOrBasePrice}
                          {priceDescriptor.suffix === '/month' ? '/mo' : priceDescriptor.suffix}
                        </Text>
                      )}
                      <Text type="regular" bold>
                        {priceDescriptor.prefix}
                        {price}
                        {priceDescriptor.suffix === '/month' ? '/mo' : priceDescriptor.suffix}
                      </Text>
                    </View>
                  </View>
                }
              />
            )
          })}
        </View>
        {selectedProduct && <PromoCard product={selectedProduct} />}
      </ScrollView>
      {selectedProduct && (
        <View style={[styles.buttonContainer, { paddingBottom: insets.bottom + 16 }]}>
          <Button type="primary" onPress={onNextPress} size="block" accessibilityLabel="Choose">
            Choose
          </Button>
        </View>
      )}
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flexGrow: 1,
    backgroundColor: 'theme.background',
  },
  scrollViewContent: {
    flexGrow: 1,
    paddingTop: 24,
    paddingHorizontal: 16,
  },
  subscriptions: {
    marginBottom: 12,
    flexDirection: 'column',
    flexWrap: 'wrap',
  },
  newSubscriptions: {
    marginTop: 32,
    gap: 8,
    marginBottom: 16,
  },
  row: {
    flexDirection: 'row',
    paddingTop: 12,
  },
  plansRow: {
    flexDirection: 'row',
    paddingBottom: 12,
    gap: 12,
  },
  subscriptionItem: {
    flex: 1,
    minHeight: 106,
  },
  includedOptionsContainer: {
    marginTop: 20,
    gap: 12,
  },
  includedOptionRow: {
    flexDirection: 'row',
  },
  icon: {
    marginRight: 12,
    color: 'theme.success.base',
  },
  optionText: {
    marginTop: 2,
    flex: 1,
  },
  priceContainer: {
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
  secondaryPrice: {
    textDecorationLine: 'line-through',
    color: 'theme.text.secondary',
  },
  buttonContainer: {
    marginHorizontal: 16,
  },
})
